import type { RrPreviewMatch, TournamentGroup } from "./api";
import { courtNoticeName } from "./courts";

/**
 * Hoja del Round Robin (Eduardo 2026-10-01): el organizador acomoda a los
 * jugadores de cada grupo (subir/bajar) antes de que empiece la categoría, y
 * el lugar 1..n define quién juega contra quién en cada jornada (1 y 2 se
 * enfrentan en la última). Aquí viven las piezas puras que comparten la
 * matriz del admin, el modo «Acomodar grupos» y el diálogo de cambio de grupo.
 */

export const RR_SHEET_PLACE_TOOLTIP = "Lugar en la hoja: define los rivales de cada jornada";

/** Orden de las filas de la matriz.
 *
 * - Sin lugares guardados (`positions` null) el resultado es IDÉNTICO al de
 *   siempre: todo por `autoCompare`, con las filas "fantasma" (bajas que
 *   solo salen de partidos) mezcladas como hoy.
 * - Con lugares: primero los miembros con lugar (por lugar), luego los
 *   miembros sin lugar (p. ej. una baja que se reincorporó) por id, igual
 *   que el generador de jornadas, y al final los fantasmas. */
export function orderMatrixParticipants<T extends { id: number }>(
  participants: T[],
  options: {
    positions: Map<number, number> | null;
    autoCompare: (a: T, b: T) => number;
    ghostIds: Set<number>;
  },
): T[] {
  const { positions, autoCompare, ghostIds } = options;
  if (!positions) return [...participants].sort(autoCompare);
  const placed: T[] = [];
  const unplaced: T[] = [];
  const ghosts: T[] = [];
  for (const participant of participants) {
    if (ghostIds.has(participant.id)) ghosts.push(participant);
    else if (positions.has(participant.id)) placed.push(participant);
    else unplaced.push(participant);
  }
  placed.sort((a, b) => (positions.get(a.id) ?? 0) - (positions.get(b.id) ?? 0) || a.id - b.id);
  unplaced.sort((a, b) => a.id - b.id);
  ghosts.sort(autoCompare);
  return [...placed, ...unplaced, ...ghosts];
}

/** Lugares guardados de un grupo; null si el grupo no está acomodado. */
export function groupSheetPositions(group: Pick<TournamentGroup, "rr_has_positions" | "participants">): Map<number, number> | null {
  if (!group.rr_has_positions) return null;
  const positions = new Map<number, number>();
  for (const assignment of group.participants ?? []) {
    if (assignment.rr_position != null) positions.set(assignment.participant_id, assignment.rr_position);
  }
  return positions.size > 0 ? positions : null;
}

/** Miembros que ocupan lugar en la hoja: asignaciones activas cuyo
 * participante no está dado de baja (mismo criterio que el backend). */
export function sheetMemberIds(group: Pick<TournamentGroup, "participants">): number[] {
  return (group.participants ?? [])
    .filter((assignment) => (assignment.status ?? "active") === "active")
    .filter((assignment) => assignment.participant?.current_status !== "dropped")
    .map((assignment) => assignment.participant_id);
}

/** Borrador inicial de «Acomodar grupos»: el orden de la hoja que manda el
 * backend (`rr_sheet_order`) o, si no hay (null: sin lugares ni partidos), el
 * orden que ya se ve en la matriz sin las filas fantasma. Siempre con los
 * miembros vigentes: se descartan ids que ya no están y los que falten van
 * al final. */
export function initialArrangeDraft(
  group: Pick<TournamentGroup, "participants" | "rr_sheet_order">,
  matrixOrderIds: number[],
  ghostIds: Set<number>,
): number[] {
  const members = sheetMemberIds(group);
  const memberSet = new Set(members);
  const source = group.rr_sheet_order ?? matrixOrderIds.filter((id) => !ghostIds.has(id));
  const seen = new Set<number>();
  const draft: number[] = [];
  for (const id of source) {
    if (memberSet.has(id) && !seen.has(id)) {
      seen.add(id);
      draft.push(id);
    }
  }
  for (const id of members) {
    if (!seen.has(id)) {
      seen.add(id);
      draft.push(id);
    }
  }
  return draft;
}

/** Sube (-1) o baja (+1) el elemento `index`; en los extremos no hace nada. */
export function moveInList(ids: number[], index: number, delta: -1 | 1): number[] {
  const target = index + delta;
  if (index < 0 || index >= ids.length || target < 0 || target >= ids.length) return ids;
  const next = [...ids];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

export function sameOrder(a: number[], b: number[]): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

/** Mismos ids sin importar el orden (el borrador sigue valiendo para el grupo). */
export function sameMembers(a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return set.size === a.length && b.every((value) => set.has(value));
}

/** "Grupo A", "Grupo A y Grupo B", "Grupo A, Grupo B y Grupo C". */
export function joinSpanishList(items: string[]): string {
  if (items.length <= 1) return items[0] ?? "";
  return `${items.slice(0, -1).join(", ")} y ${items[items.length - 1]}`;
}

const WEEKDAYS = ["dom", "lun", "mar", "mié", "jue", "vie", "sáb"];
const MONTHS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

/** "2026-10-04" → "dom 4 oct" (fecha pura, sin zona horaria). */
export function shortScheduleDate(value: string | null | undefined): string | null {
  const found = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? "");
  if (!found) return value?.trim() || null;
  const [year, month, day] = [Number(found[1]), Number(found[2]), Number(found[3])];
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  return `${WEEKDAYS[weekday]} ${day} ${MONTHS[month - 1] ?? ""}`.trim();
}

/** "09:00:00" → "9:00". */
export function shortScheduleTime(value: string | null | undefined): string | null {
  const found = /^(\d{1,2}):(\d{2})/.exec(value ?? "");
  if (!found) return value?.trim() || null;
  return `${Number(found[1])}:${found[2]}`;
}

/** "Ana López vs Eva Ruiz". */
export function previewMatchPlayers(match: Pick<RrPreviewMatch, "player1" | "player2">): string {
  return `${match.player1.name} vs ${match.player2.name}`;
}

/** Línea de un partido en la revisión de cambios:
 * "Ana López vs Eva Ruiz · dom 4 oct · 9:00 · Cancha 1" o "… · sin horario". */
export function previewMatchLine(match: RrPreviewMatch): string {
  const parts = [previewMatchPlayers(match)];
  const date = shortScheduleDate(match.scheduled_date);
  if (!date) {
    parts.push("sin horario");
  } else {
    parts.push(date);
    const time = shortScheduleTime(match.scheduled_time);
    if (time) parts.push(time);
  }
  // Mini canchas (#429): "9-A" se lee «Cancha 9-A», igual que en Rondas.
  const court = courtNoticeName(match.court);
  if (court) parts.push(court);
  if (match.venue_name?.trim()) parts.push(match.venue_name.trim());
  return parts.join(" · ");
}
